import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { MeetRoom } from "@/pages/Meet";
import { VideoCamera, ShieldCheck, LockKey } from "@phosphor-icons/react";

const asset = (u) => (u && u.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

const MODE_LABEL = { meet: "Meeting", conference: "Conference", webinar: "Webinar", classroom: "Classroom", private: "Private Room" };

export default function JoinMeet() {
  const [params] = useSearchParams();
  const code = params.get("room");
  const [info, setInfo] = useState(undefined); // undefined=loading, null=missing
  const [brand, setBrand] = useState(null);
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [joined, setJoined] = useState(false);

  useEffect(() => {
    if (!code) { setInfo(null); return; }
    api.get(`/meetings/${code}/public`).then(({ data }) => setInfo(data)).catch(() => setInfo(null));
    api.get(`/meetings/${code}/branding`).then(({ data }) => setBrand(data.branding)).catch(() => {});
  }, [code]);

  if (joined) return <MeetRoom code={code} guest guestName={name || "Guest"} pin={pin} />;

  const pinReady = !info?.pin_required || pin.length >= 1;

  return (
    <div className="grid min-h-screen place-items-center bg-[#08111F] p-6 text-white">
      <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-center" data-testid="join-page">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary" style={brand?.brand_color ? { backgroundColor: brand.brand_color } : undefined}>
          {brand?.logo_url ? <img src={asset(brand.logo_url)} alt="" className="h-full w-full rounded-2xl object-contain p-2" data-testid="join-logo" /> : <VideoCamera size={28} weight="duotone" />}
        </div>
        {brand?.brand_name && <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-white/50" data-testid="join-brand">{brand.brand_name}</p>}
        {info === undefined && <p className="mt-4 text-white/60">Loading meeting…</p>}
        {info === null && <p className="mt-4 text-white/60" data-testid="join-not-found">This meeting link is invalid or has ended.</p>}
        {info && (
          <>
            {info.mode && info.mode !== "meet" && (
              <span className="mt-4 inline-block rounded-full bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white/70" data-testid="join-mode">{MODE_LABEL[info.mode] || info.mode}</span>
            )}
            <h1 className="mt-3 font-display text-xl font-semibold">{info.title}</h1>
            {info.locked ? (
              <p className="mt-2 text-sm text-amber-400" data-testid="join-locked">This meeting is locked by the host and isn't accepting new participants right now.</p>
            ) : (
              <>
                <p className="mt-1 text-sm text-white/50">You're joining as a guest. Enter your name to continue.</p>
                <input data-testid="join-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
                  className="mt-4 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-center outline-none focus:border-primary" />
                {info.pin_required && (
                  <div className="mt-3">
                    <label className="mb-1 flex items-center justify-center gap-1.5 text-xs text-white/50"><LockKey size={13} /> This meeting requires a PIN</label>
                    <input data-testid="join-pin" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))} placeholder="Enter PIN"
                      className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-center font-mono-metric outline-none focus:border-primary" />
                  </div>
                )}
                <button data-testid="join-submit" disabled={!name.trim() || !pinReady} onClick={() => setJoined(true)} style={brand?.brand_color ? { backgroundColor: brand.brand_color } : undefined}
                  className="mt-3 w-full rounded-full bg-primary py-3 font-semibold text-primary-foreground disabled:opacity-50">Join meeting</button>
              </>
            )}
            <p className="mt-4 flex items-center justify-center gap-1.5 text-[11px] text-white/40"><ShieldCheck size={13} weight="fill" /> Secured peer-to-peer video</p>
          </>
        )}
      </div>
    </div>
  );
}
